import { useMemo, useState, type ReactNode } from "react";
import { Link as RouterLink, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Accordion, AccordionDetails, AccordionSummary, Alert, Box, Button, Checkbox, Chip, Dialog, DialogActions,
  DialogContent, DialogTitle, FormControlLabel, LinearProgress, Link, Paper, Skeleton, Stack, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TextField, Tooltip, Typography, alpha,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import RefreshIcon from "@mui/icons-material/Refresh";
import SendIcon from "@mui/icons-material/Send";
import { AppLayout } from "@/components/AppLayout";
import { useAuth } from "@/auth/AuthContext";
import { formatDate, formatDateTime } from "@/dates";
import {
  fetchLightreachImport, fetchLightreachImports, postLightreachDeposit, postLightreachProject, replanLightreachBatch,
  updateLightreachBatch, uploadLightreachExport,
  type BatchUpdate, type LrBatch, type LrFlag, type LrProject, type LrRecord,
} from "@/api/lightreach";

const usdFormat = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" });
const usd = (value: string | number | undefined | null) => usdFormat.format(Number(value ?? 0));
const plural = (count: number | undefined, noun: string) => `${count ?? 0} ${noun}${count === 1 ? "" : "s"}`;

const RECORD_LABEL: Record<LrRecord["type"], string> = {
  invoice: "True-up invoice",
  credit_memo: "Credit memo",
  existing_credit_memo: "Uses existing credit memo",
  price_correction: "Price correction",
  payment: "Payment",
  refund: "Refund (DO NOT PRINT)",
};

const WRITE_TYPES: LrRecord["type"][] = ["invoice", "credit_memo", "price_correction", "payment", "refund"];

const STATUS_CHIP: Record<LrBatch["status"], { label: string; color: "default" | "info" | "success" | "error" | "warning" }> = {
  planned: { label: "Dry run", color: "info" },
  posting: { label: "Posting…", color: "warning" },
  partly_posted: { label: "Partly posted", color: "info" },
  posted: { label: "Deposit posted", color: "success" },
  failed: { label: "Failed", color: "error" },
  already_booked: { label: "Already booked", color: "default" },
};

// "Already booked" isn't an error: someone did the work already. These read as
// neutral notes, never red.
const NEUTRAL_FLAGS = new Set(["possibly_booked", "already_posted", "partly_booked", "booked_by_hand", "inferred_row"]);

function errorMessage(error: unknown): string {
  const data = (error as { response?: { data?: { error?: string; errors?: string[] } } })?.response?.data;
  return data?.error ?? data?.errors?.join(", ") ?? (error as Error)?.message ?? "Something went wrong";
}

function netsuiteLink(base: string | null | undefined, id: number | null | undefined, label: ReactNode) {
  if (!base || !id) return label;
  return (
    <Link href={`${base}/app/accounting/transactions/transaction.nl?id=${id}`} target="_blank" rel="noopener noreferrer">
      {label}
    </Link>
  );
}

// Projects whose Activation (PTO) clawback is booked as a refund: once the
// refund posts, AR invoices the activation again so Lightreach's next PTO
// payout has something to apply to.
function ptoRefundAmount(p: LrProject): number {
  if (!p.records.some((r) => r.type === "refund")) return 0;
  return p.rows.filter((r) => r.kind === "activation_clawback").reduce((sum, r) => sum + Math.abs(Number(r.amount)), 0);
}

// ---------------------------------------------------------------------------
// /lightreach: upload + recent imports
// ---------------------------------------------------------------------------

export function LightreachImportsPage() {
  const navigate = useNavigate();
  const imports = useQuery({ queryKey: ["lightreach_imports"], queryFn: fetchLightreachImports });
  const [file, setFile] = useState<File | null>(null);

  const upload = useMutation({
    mutationFn: () => uploadLightreachExport(file as File),
    onSuccess: (created) => navigate(`/lightreach/${created.id}`),
  });

  return (
    <AppLayout>
      <Typography variant="h5" gutterBottom>Lightreach payments</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Upload Lightreach&apos;s <strong>approved export</strong> for one payout. You get a dry run: the credit memos,
        payments, DO NOT PRINT refunds and bank deposit it would book in NetSuite, dated on the Tuesday or Thursday payout
        and checked against the wire. Then post it one customer at a time, open each record in NetSuite to review it, and
        create the deposit last. Nothing is written until you post a line.
      </Typography>

      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ sm: "center" }}>
          <Button component="label" variant="outlined" startIcon={<UploadFileIcon />}>
            {file ? file.name : "Choose CSV"}
            <input hidden type="file" accept=".csv,text/csv" onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
          </Button>
          <Button variant="contained" disabled={!file || upload.isPending} onClick={() => upload.mutate()}>
            {upload.isPending ? "Uploading…" : "Upload and dry run"}
          </Button>
        </Stack>
        {upload.isError && <Alert severity="error" sx={{ mt: 2 }}>{errorMessage(upload.error)}</Alert>}
      </Paper>

      <Typography variant="h6" gutterBottom>Recent uploads</Typography>
      {imports.isLoading ? (
        <Skeleton variant="rounded" height={120} />
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Uploaded</TableCell>
                <TableCell>File</TableCell>
                <TableCell align="right">Batches</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>By</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(imports.data ?? []).map((i) => (
                <TableRow key={i.id} hover component={RouterLink} to={`/lightreach/${i.id}`} sx={{ textDecoration: "none" }}>
                  <TableCell>{formatDateTime(i.created_at)}</TableCell>
                  <TableCell>{i.filename}</TableCell>
                  <TableCell align="right">{i.batch_count}</TableCell>
                  <TableCell>{i.status}</TableCell>
                  <TableCell>{i.created_by}</TableCell>
                </TableRow>
              ))}
              {imports.data?.length === 0 && (
                <TableRow><TableCell colSpan={5}><Typography color="text.secondary">No uploads yet.</Typography></TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </AppLayout>
  );
}

// ---------------------------------------------------------------------------
// /lightreach/:id: the dry run, then posting line by line
// ---------------------------------------------------------------------------

export function LightreachImportPage() {
  const id = Number(useParams().id);
  const { user } = useAuth();
  const query = useQuery({
    queryKey: ["lightreach_import", id],
    queryFn: () => fetchLightreachImport(id),
    // Poll while planning or while a line or deposit is posting.
    refetchInterval: (q) => {
      const d = q.state.data;
      return d && (d.status === "planning" || d.batches?.some((b) => b.status === "posting")) ? 2000 : false;
    },
  });
  const data = query.data;

  return (
    <AppLayout maxWidth="xl">
      <Button component={RouterLink} to="/lightreach" size="small" sx={{ mb: 1 }}>← All uploads</Button>
      {!data ? (
        <Skeleton variant="rounded" height={240} />
      ) : (
        <>
          <Typography variant="h5">Lightreach payments: {data.filename}</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {data.cutoff_on && <>Batches approved on or after {formatDate(data.cutoff_on)} · </>}
            {data.row_count} rows in the file · uploaded by {data.created_by} on {formatDateTime(data.created_at)}
          </Typography>
          {data.status === "planning" && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Matching projects in Sunrise and reading invoices from NetSuite…
              <LinearProgress sx={{ mt: 1 }} />
            </Alert>
          )}
          {data.status === "failed" && <Alert severity="error" sx={{ mb: 2 }}>{data.error_message}</Alert>}
          {data.status === "planned" && !data.write_enabled && (
            <Alert severity="warning" sx={{ mb: 2 }}>Dry run only: NetSuite writes are turned off for Lightreach imports.</Alert>
          )}
          {data.status === "planned" && data.write_enabled && !user?.lightreach_ar_poster && (
            <Alert severity="info" sx={{ mb: 2 }}>
              You can review this dry run, but posting needs the <strong>Lightreach AR poster</strong> permission
              (Users page).
            </Alert>
          )}
          {data.status === "planned" && data.batches?.length === 0 && (
            <Alert severity="info">No approved payout batches in this file.</Alert>
          )}
          <Stack spacing={1.5}>
            {(data.batches ?? []).map((b) => (
              <BatchCard key={b.id} importId={id} batch={b} netsuiteBase={data.netsuite_base_url} writeEnabled={!!data.write_enabled} />
            ))}
          </Stack>
        </>
      )}
    </AppLayout>
  );
}

function BatchCard({ importId, batch, netsuiteBase, writeEnabled }: {
  importId: number; batch: LrBatch; netsuiteBase?: string | null; writeEnabled: boolean;
}) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const plan = batch.plan;
  const projects = plan.projects ?? [];
  const state = (p: LrProject) => batch.project_states?.[p.finco_account_id] ?? {};
  // Booked by hand in NetSuite: grouped and collapsed, not shown as errors.
  const isBookedElsewhere = (p: LrProject) => p.already_booked && p.booked_by !== "tool" && state(p).status !== "posted";
  const active = projects.filter((p) => !isBookedElsewhere(p));
  const booked = projects.filter(isBookedElsewhere);
  const blockingCount = active.reduce((n, p) => n + (state(p).status === "posted" ? 0 : p.flags.filter((f) => f.blocking).length), 0);
  const postedCount = projects.filter((p) => state(p).status === "posted").length;

  const [depositOn, setDepositOn] = useState(batch.deposit_on);
  const [confirmingDeposit, setConfirmingDeposit] = useState(false);
  const [leavingOpen, setLeavingOpen] = useState(false);
  const refresh = () => qc.invalidateQueries({ queryKey: ["lightreach_import", importId] });

  const update = useMutation({ mutationFn: (input: BatchUpdate) => updateLightreachBatch(batch.id, input), onSuccess: refresh });
  const replan = useMutation({ mutationFn: () => replanLightreachBatch(batch.id), onSuccess: refresh });
  const postProject = useMutation({ mutationFn: (finco: string) => postLightreachProject(batch.id, finco), onSuccess: refresh });
  const postDeposit = useMutation({
    mutationFn: (acknowledged?: string[]) => postLightreachDeposit(batch.id, acknowledged),
    onSuccess: () => { setConfirmingDeposit(false); setLeavingOpen(false); refresh(); },
  });
  const busy = update.isPending || replan.isPending || postProject.isPending;
  const editable = batch.status !== "posted" && batch.status !== "posting";
  const canPost = !!user?.lightreach_ar_poster && writeEnabled;
  const dateLocked = !editable || batch.status === "partly_posted";

  // What the deposit will hold: the payments this tool posted.
  const postedPayments = projects.flatMap((p) => p.records).filter((r) => r.type === "payment" && batch.posted[r.external_id]);
  const depositAmount = postedPayments.reduce((sum, r) => sum + Number(r.amount), 0);
  const handPayments = booked.flatMap((p) => p.records).filter((r) => r.type === "payment").reduce((sum, r) => sum + Number(r.amount), 0);
  // Projects the deposit went (or would go) without, to book by hand.
  const openProjects = projects.filter((p) => batch.open_projects?.includes(p.finco_account_id));
  const leftForHand = projects.filter((p) => batch.project_states?.[p.finco_account_id]?.status === "left_for_hand");
  const status = STATUS_CHIP[batch.status];

  return (
    <Accordion defaultExpanded={batch.status !== "already_booked" && batch.status !== "posted"} disableGutters variant="outlined">
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems={{ md: "center" }} sx={{ width: "100%", pr: 2 }}>
          <Box sx={{ minWidth: 200 }}>
            <Typography fontWeight={700}>Payout {formatDate(batch.deposit_on)}</Typography>
            <Typography variant="caption" color="text.secondary">
              Approved {formatDate(batch.approved_on)} · Batch …{batch.lr_batch_id.slice(-6)} · {plan.counts?.projects ?? 0} customers
            </Typography>
          </Box>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="body2">
              Deposit <strong>{usd(plan.deposit_total)}</strong> − refunds <strong>{usd(plan.refund_total)}</strong> = wire{" "}
              <strong>{usd(Number(plan.deposit_total ?? 0) - Number(plan.refund_total ?? 0))}</strong>
              {" "}· Lightreach total <strong>{usd(batch.expected_total)}</strong>
            </Typography>
          </Box>
          <Stack direction="row" spacing={1}>
            {plan.reconciled !== undefined && (
              <Chip size="small" color={plan.reconciled ? "success" : "warning"} label={plan.reconciled ? "Ties to wire" : "Doesn't tie"} />
            )}
            {postedCount > 0 && <Chip size="small" color="success" variant="outlined" label={`${postedCount} of ${projects.length} posted`} />}
            {blockingCount > 0 && editable && <Chip size="small" color="error" label={`${blockingCount} to fix`} />}
            <Chip size="small" color={status.color} label={status.label} />
          </Stack>
        </Stack>
      </AccordionSummary>
      <AccordionDetails>
        {batch.error_message && <Alert severity="error" sx={{ mb: 2 }}>{batch.error_message}</Alert>}
        {batch.posted_elsewhere && <Alert severity="warning" sx={{ mb: 2 }}>This Lightreach batch was already posted from another upload.</Alert>}
        {(plan.flags ?? []).map((f) => <FlagAlert key={f.code} flag={f} />)}

        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems={{ sm: "center" }} sx={{ mb: 2 }}>
          <TextField
            label="Payout date" type="date" size="small" value={depositOn} disabled={dateLocked || busy}
            helperText={batch.status === "partly_posted" ? "Locked: lines are already posted on this date" : "Lightreach's Tuesday or Thursday payout"}
            onChange={(e) => setDepositOn(e.target.value)} InputLabelProps={{ shrink: true }}
          />
          {depositOn !== batch.deposit_on && (
            <Button size="small" variant="outlined" disabled={busy} onClick={() => update.mutate({ deposit_on: depositOn })}>Save date</Button>
          )}
          <Button size="small" startIcon={<RefreshIcon />} disabled={!editable || busy} onClick={() => replan.mutate()}>
            {replan.isPending ? "Re-checking…" : "Re-check NetSuite"}
          </Button>
        </Stack>
        {(update.isError || replan.isError || postProject.isError) && (
          <Alert severity="error" sx={{ mb: 2 }}>{errorMessage(update.error ?? replan.error ?? postProject.error)}</Alert>
        )}

        <ProjectsTable
          projects={active} batch={batch} netsuiteBase={netsuiteBase} editable={editable} canPost={canPost}
          onUpdate={(input) => update.mutate(input)} onPost={(finco) => postProject.mutate(finco)} busy={busy}
        />

        {booked.length > 0 && (
          <Accordion disableGutters variant="outlined" sx={{ mt: 1.5, bgcolor: "action.hover" }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="body2" color="text.secondary">
                Already booked in NetSuite ({booked.length}) · nothing to post
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <ProjectsTable
                projects={booked} batch={batch} netsuiteBase={netsuiteBase} editable={false} canPost={false}
                onUpdate={() => undefined} onPost={() => undefined} busy={busy}
              />
            </AccordionDetails>
          </Accordion>
        )}

        <Paper variant="outlined" sx={{ p: 2, mt: 1.5 }}>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ sm: "center" }}>
            <Box sx={{ flexGrow: 1 }}>
              <Typography variant="body2" fontWeight={700}>Deposit (last step)</Typography>
              {batch.status === "posted" ? (
                <Typography variant="body2">
                  Created by {batch.posted_by} on {formatDateTime(batch.posted_at)} ·{" "}
                  {netsuiteLink(netsuiteBase, batch.netsuite_deposit_id, "Open the deposit in NetSuite")}
                </Typography>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  {plural(postedPayments.length, "posted payment")} totaling <strong>{usd(depositAmount)}</strong>, dated{" "}
                  {formatDate(batch.deposit_on)}, to 10810 TX Capital Deposit. Unlocks once every customer above is posted or
                  booked by hand.
                </Typography>
              )}
              {leftForHand.length > 0 && <LeftForHandList projects={leftForHand} batch={batch} />}
              {handPayments > 0 && batch.status !== "posted" && (
                <Typography variant="caption" color="text.secondary" display="block">
                  {usd(handPayments)} was booked by hand; add those payments to this deposit in NetSuite so it matches the wire.
                </Typography>
              )}
            </Box>
            {canPost && batch.status !== "posted" && !batch.deposit_ready && batch.deposit_ready_with_open_items && (
              <Button variant="outlined" color="warning" disabled={busy} onClick={() => setLeavingOpen(true)}>
                Post deposit with open items
              </Button>
            )}
            {canPost && batch.status !== "posted" && (
              <Tooltip title={batch.deposit_ready ? "" : "Post or resolve every customer first"}>
                <span>
                  <Button variant="contained" startIcon={<SendIcon />} disabled={!batch.deposit_ready || busy}
                          onClick={() => setConfirmingDeposit(true)}>
                    Create deposit
                  </Button>
                </span>
              </Tooltip>
            )}
          </Stack>
        </Paper>

        <Dialog open={confirmingDeposit} onClose={() => setConfirmingDeposit(false)}>
          <DialogTitle>Create the deposit in NetSuite?</DialogTitle>
          <DialogContent>
            <Typography gutterBottom>
              One deposit of <strong>{usd(depositAmount)}</strong> ({plural(postedPayments.length, "payment")}) dated{" "}
              {formatDate(batch.deposit_on)} to 10810 TX Capital Deposit.
            </Typography>
            <Alert severity="info" sx={{ mt: 2 }}>
              Only create it once this wire shows in the bank. If an expected Lightreach funding hasn&apos;t arrived,
              contact Lightreach instead.
            </Alert>
            {postDeposit.isError && <Alert severity="error" sx={{ mt: 2 }}>{errorMessage(postDeposit.error)}</Alert>}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmingDeposit(false)}>Cancel</Button>
            <Button variant="contained" disabled={postDeposit.isPending} onClick={() => postDeposit.mutate(undefined)}>
              {postDeposit.isPending ? "Starting…" : "Create deposit"}
            </Button>
          </DialogActions>
        </Dialog>
        {leavingOpen && (
          <OpenItemsDepositDialog projects={openProjects} depositAmount={depositAmount} paymentCount={postedPayments.length}
                                  batch={batch} pending={postDeposit.isPending} error={postDeposit.isError ? errorMessage(postDeposit.error) : null}
                                  onCancel={() => setLeavingOpen(false)} onConfirm={(fincos) => postDeposit.mutate(fincos)} />
        )}
      </AccordionDetails>
    </Accordion>
  );
}

const recordTotal = (p: LrProject, type: LrRecord["type"]) =>
  p.records.filter((r) => r.type === type).reduce((sum, r) => sum + Number(r.amount), 0);

function openReason(p: LrProject): string {
  const blocking = p.flags.find((f) => f.blocking);
  return blocking ? blocking.message : "Not posted yet.";
}

// Posting the deposit before every customer is done: the poster ticks each
// open customer they'll book by hand, and sees what the deposit will be missing.
function OpenItemsDepositDialog({ projects, depositAmount, paymentCount, batch, pending, error, onCancel, onConfirm }: {
  projects: LrProject[]; depositAmount: number; paymentCount: number; batch: LrBatch; pending: boolean; error: string | null;
  onCancel: () => void; onConfirm: (fincos: string[]) => void;
}) {
  const [ticked, setTicked] = useState<Set<string>>(new Set());
  const toggle = (finco: string) => setTicked((prev) => {
    const next = new Set(prev);
    if (next.has(finco)) next.delete(finco); else next.add(finco);
    return next;
  });
  const missingPayments = projects.reduce((sum, p) => sum + recordTotal(p, "payment"), 0);
  const missingRefunds = projects.reduce((sum, p) => sum + recordTotal(p, "refund"), 0);
  const allTicked = projects.every((p) => ticked.has(p.finco_account_id));

  return (
    <Dialog open onClose={onCancel} maxWidth="sm" fullWidth>
      <DialogTitle>Create the deposit with {plural(projects.length, "open customer")}?</DialogTitle>
      <DialogContent>
        <Typography gutterBottom>
          The deposit will be <strong>{usd(depositAmount)}</strong> ({plural(paymentCount, "payment")}). Nothing is posted for the
          customers below; tick each one you&apos;ll book by hand in NetSuite.
        </Typography>
        <Stack spacing={1} sx={{ my: 2 }}>
          {projects.map((p) => {
            const payment = recordTotal(p, "payment");
            const refund = recordTotal(p, "refund");
            return (
              <Paper key={p.finco_account_id} variant="outlined" sx={{ p: 1.5 }}>
                <FormControlLabel
                  control={<Checkbox checked={ticked.has(p.finco_account_id)} onChange={() => toggle(p.finco_account_id)} />}
                  label={
                    <Box>
                      <Typography variant="body2" fontWeight={700}>
                        Project {p.project_id ?? "?"} · {p.customer.netsuite_name ?? (p.customer.lightreach_name || "No customer yet")}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block">{openReason(p)}</Typography>
                      <Typography variant="caption" display="block">
                        I&apos;ll book this by hand
                        {payment > 0 && <>, including the <strong>{usd(payment)}</strong> payment</>}
                        {refund > 0 && <>, including the <strong>{usd(refund)}</strong> refund</>}.
                      </Typography>
                    </Box>
                  }
                />
              </Paper>
            );
          })}
        </Stack>
        {missingPayments > 0 && (
          <Alert severity="warning">
            Add {usd(missingPayments)} of payments to this deposit in NetSuite once they&apos;re booked
            {missingRefunds > 0 && <>, and book the {usd(missingRefunds)} of refunds</>}, so deposit minus refunds matches the
            wire ({usd(batch.expected_total)}).
          </Alert>
        )}
        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel}>Cancel</Button>
        <Button variant="contained" disabled={!allTicked || pending} onClick={() => onConfirm(projects.map((p) => p.finco_account_id))}>
          {pending ? "Starting…" : "Create deposit"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// After the deposit went without them: what's still to book by hand.
function LeftForHandList({ projects, batch }: { projects: LrProject[]; batch: LrBatch }) {
  return (
    <Alert severity="warning" sx={{ mt: 1 }}>
      <Typography variant="body2" fontWeight={700}>To book by hand in NetSuite</Typography>
      {projects.map((p) => {
        const state = batch.project_states?.[p.finco_account_id];
        const payment = recordTotal(p, "payment");
        const refund = recordTotal(p, "refund");
        return (
          <Typography key={p.finco_account_id} variant="body2">
            Project {p.project_id ?? "?"} {p.customer.netsuite_name ?? p.customer.lightreach_name}
            {payment > 0 && ` · payment ${usd(payment)} (add it to the deposit)`}
            {refund > 0 && ` · refund ${usd(refund)}`}
            <Typography component="span" variant="caption" color="text.secondary">
              {state?.acknowledged_by && ` · acknowledged by ${state.acknowledged_by}`}
              {state?.acknowledged_at && ` on ${formatDateTime(state.acknowledged_at)}`}
            </Typography>
          </Typography>
        );
      })}
    </Alert>
  );
}

function FlagAlert({ flag }: { flag: LrFlag }) {
  if (NEUTRAL_FLAGS.has(flag.code) && !flag.blocking) {
    return (
      <Alert severity="info" icon={false} sx={{ mb: 1, bgcolor: "action.hover", color: "text.secondary" }}>{flag.message}</Alert>
    );
  }
  return <Alert severity={flag.blocking ? "error" : "warning"} sx={{ mb: 1 }}>{flag.message}</Alert>;
}

function ProjectsTable({ projects, batch, netsuiteBase, editable, canPost, onUpdate, onPost, busy }: {
  projects: LrProject[]; batch: LrBatch; netsuiteBase?: string | null; editable: boolean; canPost: boolean;
  onUpdate: (input: BatchUpdate) => void; onPost: (finco: string) => void; busy: boolean;
}) {
  const [confirming, setConfirming] = useState<LrProject | null>(null);
  // Problems first, then by name, so the reviewer works top-down.
  const sorted = useMemo(
    () => [...projects].sort((a, b) => Number(b.blocked) - Number(a.blocked) ||
      a.customer.lightreach_name.localeCompare(b.customer.lightreach_name)),
    [projects],
  );

  return (
    <>
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Customer / project</TableCell>
              <TableCell>Lightreach paid</TableCell>
              <TableCell>NetSuite invoice</TableCell>
              <TableCell>Will book</TableCell>
              <TableCell>Post</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sorted.map((p) => {
              const state = batch.project_states?.[p.finco_account_id] ?? {};
              const posted = state.status === "posted";
              const pto = ptoRefundAmount(p);
              return (
                <TableRow key={p.finco_account_id} sx={{
                  verticalAlign: "top",
                  bgcolor: posted ? (t) => alpha(t.palette.success.main, 0.06)
                    : p.blocked && !p.already_booked ? (t) => alpha(t.palette.error.main, 0.06) : undefined,
                }}>
                  <TableCell sx={{ minWidth: 200 }}>
                    <Typography variant="body1" fontWeight={700}>
                      Project {p.project_id ?? "?"}
                      {p.project_source === "manual" && (
                        <Typography component="span" variant="caption" color="text.secondary"> (entered by hand)</Typography>
                      )}
                    </Typography>
                    <Typography variant="body2">{p.customer.netsuite_name ?? (p.customer.lightreach_name || "No customer yet")}</Typography>
                    <Typography variant="caption" color="text.secondary" display="block">{p.customer.address}</Typography>
                    {editable && !posted && p.project_id && <ChangeProject project={p} onUpdate={onUpdate} busy={busy} />}
                  </TableCell>
                  <TableCell sx={{ minWidth: 220 }}>
                    {p.rows.map((r) => {
                      const offset = p.offset_transaction_ids?.includes(r.transaction_id);
                      return (
                        <Box key={r.transaction_id}>
                          <Typography variant="body2" color={Number(r.amount) < 0 ? "error.main" : undefined}
                                      sx={offset ? { textDecoration: "line-through", opacity: 0.6 } : undefined}>
                            {usd(r.amount)} <Typography component="span" variant="caption" color="text.secondary">
                              {r.description}{offset && " (cancels out, not booked)"}{r.inferred_from && " (matched from the row above)"}
                              {r.detached && " (moved off the customer above)"}
                            </Typography>
                          </Typography>
                          {r.inferred_from && editable && !posted && (
                            <ConfirmButton label="Wrong customer" busy={busy}
                                           title={`Move ${usd(r.amount)} off project ${p.project_id ?? "?"}?`}
                                           body="The row becomes its own card, and you enter the project it belongs to. The rest of this customer stays as it is."
                                           confirmLabel="Move it"
                                           onConfirm={() => onUpdate({ project: { finco_account_id: p.finco_account_id, detach_row: r.transaction_id } })} />
                          )}
                        </Box>
                      );
                    })}
                    <Typography variant="caption" fontWeight={600}>Net {usd(p.net_total)}</Typography>
                  </TableCell>
                  <TableCell sx={{ minWidth: 150 }}>
                    {p.invoices.map((inv) => (
                      <Typography key={inv.id} variant="body2">
                        {netsuiteLink(netsuiteBase, inv.id, `#${inv.tranid}`)}{" "}
                        <Typography component="span" variant="caption" color="text.secondary">
                          {formatDate(inv.trandate)} · open {usd(inv.unpaid)} of {usd(inv.total)}
                        </Typography>
                      </Typography>
                    ))}
                  </TableCell>
                  <TableCell sx={{ minWidth: 320 }}>
                    {p.records.map((r) => (
                      <Box key={r.key} sx={{ mb: 0.5 }}>
                        <Typography variant="body2">
                          <strong>{RECORD_LABEL[r.type]}</strong>{" "}
                          {r.type === "existing_credit_memo" ? netsuiteLink(netsuiteBase, r.netsuite_id, `#${r.tranid}`) : null} {usd(r.amount)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block">
                          “{r.memo}”{r.item_name && ` · ${r.item_name}`}
                          {r.applies.length > 0 && ` · applied to ${r.applies.map((a) => a.tranid ? `#${a.tranid}` : r.type === "refund" ? "its credit memo" : "the true-up invoice").join(", ")}`}
                          {r.type === "credit_memo" && r.applies.length === 0 && " · left open for the refund"}
                          {r.type === "existing_credit_memo" && " · already in NetSuite, not rebooked"}
                        </Typography>
                      </Box>
                    ))}
                    {!posted && p.flags.map((f) => <FlagAlert key={f.code} flag={f} />)}
                    {pto > 0 && (
                      <Alert severity={posted ? "warning" : "info"} sx={{ mt: 1 }}>
                        {posted ? "Reminder: " : "After this posts: "}create a new invoice for project {p.project_id ?? "?"} for the{" "}
                        {usd(pto)} activation (PTO) amount, so Lightreach&apos;s next PTO payout has an invoice to apply to.
                      </Alert>
                    )}
                    {editable && !posted && <ProjectFixes project={p} onUpdate={onUpdate} busy={busy} />}
                  </TableCell>
                  <TableCell sx={{ minWidth: 170 }}>
                    <ProjectPostCell project={p} batch={batch} netsuiteBase={netsuiteBase} canPost={canPost} busy={busy}
                                     onPost={() => setConfirming(p)} />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={!!confirming} onClose={() => setConfirming(null)}>
        <DialogTitle>Post {confirming?.customer.netsuite_name ?? confirming?.customer.lightreach_name} to NetSuite?</DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            Creates {confirming?.records.filter((r) => WRITE_TYPES.includes(r.type))
              .map((r) => `${RECORD_LABEL[r.type].toLowerCase()} ${usd(r.amount)}`).join(", ")}, dated{" "}
            {formatDate(batch.deposit_on)}.
          </Typography>
          <Typography color="text.secondary">
            NetSuite is checked again first; if anything changed for this customer, nothing is written. Each record gets a link
            here to review it in NetSuite. The deposit is created separately, once every customer is done.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirming(null)}>Cancel</Button>
          <Button variant="contained" onClick={() => { if (confirming) onPost(confirming.finco_account_id); setConfirming(null); }}>
            Post
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

function ProjectPostCell({ project, batch, netsuiteBase, canPost, busy, onPost }: {
  project: LrProject; batch: LrBatch; netsuiteBase?: string | null; canPost: boolean; busy: boolean; onPost: () => void;
}) {
  const state = batch.project_states?.[project.finco_account_id] ?? {};
  const written = project.records.filter((r) => WRITE_TYPES.includes(r.type) && batch.posted[r.external_id]);

  const links = written.length > 0 && (
    <Stack spacing={0.25} sx={{ mt: 0.5 }}>
      {written.map((r) => (
        <Typography key={r.key} variant="caption">
          {netsuiteLink(netsuiteBase, batch.posted[r.external_id], `Open ${RECORD_LABEL[r.type].toLowerCase()}`)}
        </Typography>
      ))}
    </Stack>
  );

  if (state.status === "posted" || project.booked_by === "tool") {
    return (
      <>
        <Chip size="small" color="success" label="Posted" />
        {state.posted_at && <Typography variant="caption" color="text.secondary" display="block">{formatDateTime(state.posted_at)}</Typography>}
        {links}
      </>
    );
  }
  if (state.status === "posting") {
    return (
      <>
        <Chip size="small" color="warning" label="Posting…" />
        <LinearProgress sx={{ mt: 1 }} />
        {links}
      </>
    );
  }
  if (project.already_booked) return <Chip size="small" label="Already booked" />;
  if (state.status === "left_for_hand") return <Chip size="small" color="warning" variant="outlined" label="Book by hand" />;

  const postable = !!batch.postable?.[project.finco_account_id];
  const failed = state.status === "failed";
  return (
    <>
      {failed ? <Chip size="small" color="error" label="Failed" />
        : project.blocked ? <Chip size="small" color="error" variant="outlined" label="Needs fixing" />
          : <Chip size="small" color="info" variant="outlined" label="Ready" />}
      {state.error && <Typography variant="caption" color={failed ? "error" : "text.secondary"} display="block" sx={{ mt: 0.5 }}>{state.error}</Typography>}
      {links}
      {canPost && (
        <Box sx={{ mt: 1 }}>
          <Button size="small" variant="contained" startIcon={<SendIcon />} disabled={!postable || busy} onClick={onPost}>
            {failed ? "Retry" : "Post"}
          </Button>
        </Box>
      )}
    </>
  );
}

function ProjectFixes({ project, onUpdate, busy }: { project: LrProject; onUpdate: (input: BatchUpdate) => void; busy: boolean }) {
  const codes = project.flags.filter((f) => f.blocking).map((f) => f.code);
  const finco = project.finco_account_id;
  const bookedByHand = project.flags.some((f) => f.code === "booked_by_hand");

  let fix: ReactNode = null;
  if (codes.includes("unmatched_project") || codes.includes("no_invoice")) {
    fix = <ProjectNumberField finco={finco} onUpdate={onUpdate} busy={busy} />;
  } else if (codes.includes("inferred_row")) {
    fix = (
      <Button size="small" variant="outlined" disabled={busy}
              onClick={() => onUpdate({ project: { finco_account_id: finco, accept_inferred_rows: true } })}>
        Confirm: belongs to project {project.project_id}
      </Button>
    );
  } else if (codes.includes("price_difference")) {
    fix = (
      <Button size="small" variant="outlined" disabled={busy}
              onClick={() => onUpdate({ project: { finco_account_id: finco, accept_price_difference: true } })}>
        Accept the difference (no correction)
      </Button>
    );
  } else if (codes.includes("fee_on_invoice")) {
    fix = (
      <Button size="small" variant="outlined" disabled={busy}
              onClick={() => onUpdate({ project: { finco_account_id: finco, accept_fee_on_invoice: true } })}>
        Fee line already reversed, continue
      </Button>
    );
  }

  return (
    <Stack spacing={1} sx={{ mt: 1 }} alignItems="flex-start">
      {fix}
      {project.blocked && !project.already_booked && (
        <ConfirmButton label="I booked this by hand in NetSuite" busy={busy}
                       title={`Mark project ${project.project_id ?? "?"} as booked by hand?`}
                       body="Nothing will be posted for this customer. Use this only if accounting already booked it in NetSuite."
                       confirmLabel="Mark booked by hand"
                       onConfirm={() => onUpdate({ project: { finco_account_id: finco, booked_by_hand: true } })} />
      )}
      {bookedByHand && (
        <Button size="small" variant="outlined" disabled={busy}
                onClick={() => onUpdate({ project: { finco_account_id: finco, booked_by_hand: false } })}>
          Undo booked by hand
        </Button>
      )}
    </Stack>
  );
}

function ProjectNumberField({ finco, onUpdate, busy, onDone }: {
  finco: string; onUpdate: (input: BatchUpdate) => void; busy: boolean; onDone?: () => void;
}) {
  const [projectId, setProjectId] = useState("");
  return (
    // Wraps so the buttons drop below the field in the narrow customer column
    // instead of squeezing it.
    <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" alignItems="center">
      <TextField size="small" label="Sunrise project #" value={projectId} sx={{ width: 180 }}
                 slotProps={{ htmlInput: { inputMode: "numeric" } }}
                 onChange={(e) => setProjectId(e.target.value.trim())} />
      <Stack direction="row" spacing={1}>
        <Button size="small" variant="contained" disabled={!projectId || busy} sx={{ whiteSpace: "nowrap" }}
                onClick={() => { onUpdate({ project: { finco_account_id: finco, sunrise_project_id: projectId } }); onDone?.(); }}>
          Use project
        </Button>
        {onDone && <Button size="small" variant="outlined" onClick={onDone}>Cancel</Button>}
      </Stack>
    </Stack>
  );
}

// A matched project can be corrected; for a real Lightreach account the new
// match is also remembered for later uploads.
function ChangeProject({ project, onUpdate, busy }: { project: LrProject; onUpdate: (input: BatchUpdate) => void; busy: boolean }) {
  const [open, setOpen] = useState(false);
  if (open) return <Box sx={{ mt: 1 }}><ProjectNumberField finco={project.finco_account_id} onUpdate={onUpdate} busy={busy} onDone={() => setOpen(false)} /></Box>;
  return (
    <Button size="small" variant="outlined" sx={{ mt: 1 }} disabled={busy} onClick={() => setOpen(true)}>
      Change project
    </Button>
  );
}

// An outlined button that asks before doing something easy to click by mistake.
function ConfirmButton({ label, title, body, confirmLabel, onConfirm, busy }: {
  label: string; title: string; body: string; confirmLabel: string; onConfirm: () => void; busy: boolean;
}) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button size="small" variant="outlined" color="inherit" sx={{ mt: 0.5 }} disabled={busy} onClick={() => setOpen(true)}>
        {label}
      </Button>
      <Dialog open={open} onClose={() => setOpen(false)}>
        <DialogTitle>{title}</DialogTitle>
        <DialogContent><Typography>{body}</Typography></DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={() => { onConfirm(); setOpen(false); }}>{confirmLabel}</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
